import {
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
  Tooltip,
  Theme,
  Button,
} from "@fluentui/react-components";
import { Edit20Regular, EditFilled } from "@fluentui/react-icons";
import * as React from "react";
import { UsedCouponDto } from "../../pages/admin/UsedCoupons";
import { formatDate } from "../../utils/HelperFunctions";

const getActiveStyle = (isActive: boolean): React.CSSProperties => {
  if (isActive) {
    return {
      backgroundColor: "#22c55e",
      color: "white",
      padding: "2px 6px",
      borderRadius: "4px",
    };
  } else {
    return {
      backgroundColor: "#ef4444",
      color: "white",
      padding: "2px 6px",
      borderRadius: "4px",
    };
  }
};

export const getColumnsUsedCoupons = (
  theme: Theme,
  navigate: (path: string) => void
): TableColumnDefinition<UsedCouponDto>[] => {
  return [
    createTableColumn<UsedCouponDto>({
      columnId: "couponCode",
      compare: (a, b) => a.coupon.couponCode.localeCompare(b.coupon.couponCode),
      renderHeaderCell: () => "Kupon kód",
      renderCell: (item) => (
        <TableCellLayout truncate>{item.coupon.couponCode}</TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "discount",
      compare: (a, b) => a.coupon.discount - b.coupon.discount,
      renderHeaderCell: () => "Kedvezmény (%)",
      renderCell: (item) => (
        <TableCellLayout truncate>{`${item.coupon.discount}%`}</TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "monthPrice",
      compare: (a, b) => (a.monthPrice ?? 0) - (b.monthPrice ?? 0),
      renderHeaderCell: () => "Kulcs-Flow Core alapmodul ára (Ft)",
      renderCell: (item) => (
        <TableCellLayout truncate>{`${
          item.monthPrice ?? "-"
        } Ft`}</TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "creationDateTime",
      compare: (a, b) =>
        a.coupon.creationDateTime.localeCompare(b.coupon.creationDateTime),
      renderHeaderCell: () => "Létrehozás dátuma",
      renderCell: (item) => (
        <TableCellLayout truncate>
          {formatDate(item.coupon.creationDateTime)}
        </TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "usedCount",
      compare: (a, b) => (a.usedCount ?? 0) - (b.usedCount ?? 0),
      renderHeaderCell: () => "Felhasznált mennyiség",
      renderCell: (item) => {
        const count = (item as any).usedCount;
        return (
          <TableCellLayout truncate>
            {count !== null && count !== undefined ? String(count) : "-"}
          </TableCellLayout>
        );
      },
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "couponType",
      compare: (a, b) => a.coupon.couponType - b.coupon.couponType,
      renderHeaderCell: () => "Típus",
      renderCell: (item) => (
        <TableCellLayout truncate>
          {getCouponTypeName(item.coupon.couponType)}
        </TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "modulName",
      compare: (a, b) => (a.modulName ?? "").localeCompare(b.modulName ?? ""),
      renderHeaderCell: () => "Modul",
      renderCell: (item) => (
        <TableCellLayout truncate>{item.modulName ?? "-"}</TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "isActive",
      compare: (a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1),
      renderHeaderCell: () => "Aktív",
      renderCell: (item) => (
        <TableCellLayout>
          {isCouponActive(item) ? (
            <span style={{ color: "green", fontWeight: 600 }}>Igen ✔</span>
          ) : (
            <span style={{ color: "red", fontWeight: 600 }}>Nem ✘</span>
          )}
        </TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,

    createTableColumn<UsedCouponDto>({
      columnId: "actions",
      renderHeaderCell: () => "Műveletek",
      renderCell: (item) => (
        <TableCellLayout truncate>
          <div style={{ display: "flex", gap: "6px" }}>
            <Tooltip content="Kupon szerkesztése" relationship="label">
              <Button
                style={{ backgroundColor: "#f0ad4e", color: "white" }}
                icon={<Edit20Regular />}
                onClick={() => {
                  navigate(`/admin/coupon/${item.coupon.id}/edit`);
                }}
              />
            </Tooltip>
          </div>
        </TableCellLayout>
      ),
    }) as TableColumnDefinition<UsedCouponDto>,
  ] as TableColumnDefinition<UsedCouponDto>[];
};

export const columnSizingOptionsUsedCoupons = {
  couponCode: { minWidth: 100, defaultWidth: 120 },
  discount: { minWidth: 70, defaultWidth: 90 },
  monthPrice: { minWidth: 120, defaultWidth: 180 },
  creationDateTime: { minWidth: 120, defaultWidth: 150 },
  usedCount: { minWidth: 100, defaultWidth: 120 },
  couponType: { minWidth: 60, defaultWidth: 80 },
  modulName: { minWidth: 100, defaultWidth: 120 },
  isActive: { minWidth: 60, defaultWidth: 80 },
  actions: { minWidth: 50, defaultWidth: 80 },
};

const getCouponTypeName = (type: number): string => {
  switch (type) {
    case 0:
      return "Mindenre";
    case 1:
      return "Csak alapmodulra";
    case 2:
      return "Csak kiegészítő modulokra";
    case 3:
      return "Csak modulra";
    case 4:
      return "Egyszer használható, csak alapmodulra"; // OnlyBaseOnlyOneUser
    default:
      return "Ismeretlen kupon típus";
  }
};

const isCouponActive = (usedCoupon: UsedCouponDto): boolean => {
  // A kupon adatok az 'usedCoupon.coupon' mezőben vannak
  const coupon = usedCoupon.coupon;

  const now = new Date();

  if (coupon.expiredDate) {
    const expired = new Date(coupon.expiredDate);
    if (expired < now) {
      return false;
    }
  }

  if (coupon.validTo) {
    const validTo = new Date(coupon.validTo);
    if (validTo >= now) {
      return true;
    }
  }

  if (coupon.validDays != null && coupon.validDays > 0) {
    if (coupon.creationDateTime) {
      const created = new Date(coupon.creationDateTime);
      const validUntil = new Date(created);

      validUntil.setDate(validUntil.getDate() + coupon.validDays);

      return validUntil >= now;
    }
  }

  return false;
};
